import { useEffect, type ReactNode } from "react";
import {
  createRootRoute,
  Link,
  Outlet,
  Scripts,
  HeadContent,
  ClientOnly,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { getServerSession } from "@/lib/session";
import { getServerRegion } from "@/lib/server-data";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { UserNav } from "@/components/user-nav";
import { Footer } from "@/components/footer";
import { ErrorFallback } from "@/components/error-fallback";
import { Toaster } from "@/components/ui/sonner";
import appCss from "../index.css?url";
import * as Sentry from "@sentry/tanstackstart-react";

// Create QueryClient with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Root document shell
function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => (
            <ErrorFallback error={error} resetError={resetError} />
          )}
          showDialog
        >
          {children}
        </Sentry.ErrorBoundary>
        <Scripts />
      </body>
    </html>
  );
}

// Credit display removed - now shown inline where needed (Dashboard, Generation Detail, Billing)

// Inner layout component
function InnerLayout() {
  const { session } = Route.useRouteContext();

  // Sync user context with Sentry for error tracking
  useEffect(() => {
    if (session?.user) {
      Sentry.setUser({
        id: session.user.id,
        username: session.user.name,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [session]);

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <header className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <nav className="container mx-auto px-4 h-16 flex items-center gap-6">
            {!session && (
              <Link
                to="/"
                preload="intent"
                className="font-semibold text-lg hover:text-foreground/80 transition-colors"
              >
                Images AI
              </Link>
            )}

            {session ? (
              <>
                <Link
                  to="/dashboard"
                  preload="intent"
                  className="font-semibold text-lg hover:text-foreground/80 transition-colors"
                >
                  Images AI
                </Link>
                <div className="ml-auto flex items-center gap-2">
                  {/* Credit display removed - now shown inline where needed */}
                  <ThemeToggle />
                  <UserNav user={session.user} />
                </div>
              </>
            ) : (
              <div className="ml-auto flex items-center gap-2">
                <ThemeToggle />
                <Link to="/login" preload="intent">
                  <Button variant="ghost" size="sm" className="rounded-full">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup" preload="intent">
                  <Button size="sm" className="rounded-full">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </nav>
        </header>

        <main className="min-h-screen bg-background">
          <Outlet />
        </main>

        <Footer />

        <ClientOnly fallback={null}>
          <Toaster />
        </ClientOnly>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export const Route = createRootRoute({
  beforeLoad: async () => {
    const session = await getServerSession();

    // Get region from Cloudflare headers (fetched once at root level)
    const region = await getServerRegion();

    // Balance is now fetched client-side in pages where needed (Dashboard, Generation Detail, Billing)
    // This eliminates duplicate fetches and reduces backend load
    return { session: session ?? null, region };
  },
  component: InnerLayout,
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1.0",
      },
      {
        title: "Images AI — AI Image Generator & Editor | Generate, Edit & Manage AI Images",
      },
      {
        name: "description",
        content:
          "AI image generator and editor with flagship models — FLUX 3, GPT Image 2.5, Qwen Image 3, Seedream 5, Ideogram V4, Nano Banana Pro, and Recraft. Edit with natural language, track versions, export anywhere. Free to start.",
      },
      {
        name: "keywords",
        content:
          "AI image generator, text to image, AI image editor, AI image editing, FLUX 3, GPT Image 2.5, Qwen Image 3, Nano Banana Pro, Ideogram V4, Seedream 5, Recraft V4.1, Riverflow 2.5, AI logo generator, AI image generator with text, photorealistic AI images, free AI image generator",
      },
      {
        name: "author",
        content: "Images AI",
      },
      {
        name: "robots",
        content: "index, follow",
      },
      {
        name: "language",
        content: "English",
      },
      // Open Graph / Facebook
      {
        property: "og:type",
        content: "website",
      },
      {
        property: "og:url",
        content: "https://images-ai.app/",
      },
      {
        property: "og:title",
        content:
          "Images AI — AI Image Generator & Editor | Generate, Edit & Manage AI Images",
      },
      {
        property: "og:description",
        content:
          "AI image generator and editor with flagship models — FLUX 3, GPT Image 2.5, Qwen Image 3, Seedream 5, Ideogram V4, Nano Banana Pro, and Recraft. Edit with natural language, track versions, export anywhere. Free to start.",
      },
      {
        property: "og:image",
        content: "https://images-ai.app/og-image.png",
      },
      {
        property: "og:image:width",
        content: "1200",
      },
      {
        property: "og:image:height",
        content: "630",
      },
      {
        property: "og:site_name",
        content: "Images AI",
      },
      // Twitter
      {
        property: "twitter:card",
        content: "summary_large_image",
      },
      {
        property: "twitter:url",
        content: "https://images-ai.app/",
      },
      {
        property: "twitter:title",
        content:
          "Images AI — AI Image Generator & Editor | Generate, Edit & Manage AI Images",
      },
      {
        property: "twitter:description",
        content:
          "AI image generator and editor with flagship models — FLUX 3, GPT Image 2.5, Qwen Image 3, Seedream 5, Ideogram V4, Nano Banana Pro, and Recraft. Edit with natural language, track versions, export anywhere. Free to start.",
      },
      {
        property: "twitter:image",
        content: "https://images-ai.app/og-image.png",
      },
      // Theme and mobile
      {
        name: "theme-color",
        content: "#000000",
      },
      {
        name: "apple-mobile-web-app-capable",
        content: "yes",
      },
      {
        name: "apple-mobile-web-app-status-bar-style",
        content: "black",
      },
      {
        name: "apple-mobile-web-app-title",
        content: "Images AI",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "canonical",
        href: "https://images-ai.app/",
      },
      // Favicons
      {
        rel: "icon",
        type: "image/x-icon",
        href: "/favicon.ico",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "192x192",
        href: "/android-chrome-192x192.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "512x512",
        href: "/android-chrome-512x512.png",
      },
      {
        rel: "preconnect",
        href: "https://fonts.googleapis.com",
      },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Manrope:wght@300;400;500;600;700&display=swap",
        onload: "this.onload=null;this.rel='stylesheet'",
      },
      // Resource hints for late-discovered third-party origins
      {
        rel: "dns-prefetch",
        href: "https://www.youtube-nocookie.com",
      },
      {
        rel: "preconnect",
        href: "https://www.youtube-nocookie.com",
      },
      {
        rel: "dns-prefetch",
        href: "https://pa.harshil.app",
      },
    ],
  }),
  scripts: () => [
    {
      src: "https://pa.harshil.app/js/pa-wl8R9k2C7Fo1gN1GypNSn.js",
      defer: true,
    },
    {
      children: `window.plausible=window.plausible||function(){(plausible.q=plausible.q||[]).push(arguments)},plausible.init=plausible.init||function(i){plausible.o=i||{}};
        plausible.init()`,
    },
  ],
  shellComponent: RootDocument,
});
